import { useQuery } from '@tanstack/react-query';
import { CookieIcon } from 'lucide-react';
import { useEffect, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { LegalLink } from '@/components/LegalLink';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useBranding } from '@/lib/branding';
import { openCookieSettings } from '@/lib/consent';
import { parseLegalText } from './legalText';

/**
 * Public legal pages (/legal/privacy, /legal/terms) in the wide sign-in shell without the demo:
 * a table of contents, numbered sections, and a footer that links the two documents and re-opens
 * the cookie notice. The copy describes what Crewline actually does (one sign-in cookie, per-job
 * texting with consent records and STOP handling) and should be reviewed by counsel before a
 * customer relies on it; the effective date is set where the documents are defined below.
 */

const EFFECTIVE = 'September 23, 2026';

interface Section {
  id: string;
  title: string;
  body: ReactNode;
}

/** Platform wording unless the operations console stored this organization's own documents (tenant → Legal). */
function useTenantLegal() {
  return useQuery({ queryKey: ['tenant', 'legal'], queryFn: () => api.tenant.legal(), staleTime: 0, refetchOnMount: 'always' });
}

function Doc({ eyebrow, title, description, sections, other, effective = EFFECTIVE }: { eyebrow: string; title: string; description: ReactNode; sections: Section[]; other: { href: string | null; fallback: string; label: string }; effective?: string }) {
  const { status } = useAuth();
  const { branding } = useBranding();
  const signedIn = status === 'authenticated';
  return (
    <AuthLayout
      demo={false}
      wide
      eyebrow={eyebrow}
      title={title}
      description={description}
      footer={
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <LegalLink href={other.href} fallback={other.fallback} className="hover:text-foreground underline underline-offset-4">
            {other.label}
          </LegalLink>
          <LegalLink href={branding.legal.signupUrl} fallback="/opt-in" className="hover:text-foreground underline underline-offset-4">
            Job-site enrollment
          </LegalLink>
          <button type="button" onClick={openCookieSettings} className="hover:text-foreground inline-flex items-center gap-1.5 underline underline-offset-4">
            <CookieIcon className="size-3.5" aria-hidden /> Cookie settings
          </button>
          <Link to={signedIn ? '/jobs' : '/login'} className="hover:text-foreground underline underline-offset-4">
            {signedIn ? 'Back to your jobs' : 'Back to sign in'}
          </Link>
        </span>
      }
      tenantLine={<>Effective {effective}.</>}
    >
      <nav aria-label="Contents" className="bg-muted/40 mb-8 border px-5 py-4">
        <p className="text-muted-foreground mb-2 text-[11px] font-medium tracking-wide uppercase">Contents</p>
        <ol className="grid gap-1 text-sm sm:grid-cols-2">
          {sections.map((s, i) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="hover:text-foreground text-muted-foreground inline-flex gap-2 underline-offset-4 hover:underline">
                <span className="tabular-nums">{i + 1}.</span>
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
      <div className="space-y-8 text-sm leading-relaxed [&_li]:mt-1 [&_p+p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5">
        {sections.map((s, i) => (
          <section key={s.id} id={s.id} className="scroll-mt-6">
            <h2 className="font-display mb-3 flex items-baseline gap-3 text-2xl tracking-tight">
              <span className="text-muted-foreground text-base tabular-nums">{String(i + 1).padStart(2, '0')}</span>
              {s.title}
            </h2>
            <div className="text-foreground/90">{s.body}</div>
          </section>
        ))}
      </div>
    </AuthLayout>
  );
}

// ------------------------------------------------------------------------------------------------

export function PrivacyPage() {
  const { branding } = useBranding();
  const app = branding.appName;
  const sections: Section[] = [
    {
      id: 'who',
      title: 'Who we are and what this covers',
      body: (
        <>
          <p>
            {app} is a job-site messaging platform: organizations (our customers) reach the people working on a job by text message from a dedicated phone number. This policy explains what we collect when you use the web app, when you receive texts from a job, and when you text a job number, and what we do with it.
          </p>
          <p>
            We act on behalf of the organization that runs your job. That organization decides who is on a contact list and what is sent; we process the data to deliver the service and as described here. Questions about a specific job or list should go to that organization first.
          </p>
        </>
      ),
    },
    {
      id: 'collect',
      title: 'What we collect',
      body: (
        <>
          <p>Depending on how you use the service, we hold:</p>
          <ul>
            <li>
              <span className="font-medium">Account details</span> for people who sign in: name, work email, mobile number, role and job assignments, sign-in events and security settings.
            </li>
            <li>
              <span className="font-medium">Contact records</span> uploaded or entered by an organization: name, mobile number, company, trade, preferred language, tags and notes.
            </li>
            <li>
              <span className="font-medium">Messages</span> sent from a job and replies texted to a job number, with delivery status, timestamps and the sending number.
            </li>
            <li>
              <span className="font-medium">Consent and opt-out records</span>: how and when a number joined a list (import, staff entry, texting a sign-up keyword) and every STOP or START received.
            </li>
            <li>
              <span className="font-medium">Site information</span> attached to a job by the organization, such as an address, used for weather notices and local-time schedules.
            </li>
            <li>
              <span className="font-medium">Technical data</span> needed to run and secure the service: IP address, browser type, and an audit log of administrative actions.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: 'use',
      title: 'How we use it',
      body: (
        <>
          <p>We use this information to deliver texts and replies, translate messages into a recipient&rsquo;s preferred language when the organization enables it, honor opt-outs, show delivery reports, keep accounts secure, provide support, and meet carrier and legal obligations.</p>
          <p>We do not use contact lists or message content to build marketing profiles, and we do not send our own promotional messages to job contacts.</p>
        </>
      ),
    },
    {
      id: 'sms',
      title: 'Text messaging, consent and opting out',
      body: (
        <>
          <p>Job texts are operational notices for the people on that job: schedule changes, gate and parking information, safety alerts and similar. Message frequency varies by job. Message and data rates may apply.</p>
          <ul>
            <li>Reply <span className="font-medium">STOP</span> to any job number to stop receiving texts from that job. You will get one confirmation and nothing further unless you reply START.</li>
            <li>Reply <span className="font-medium">HELP</span> for the purpose of the number and the information keywords it answers.</li>
            <li>We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.</li>
                <li>No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'share',
      title: 'Who we share it with',
      body: (
        <>
          <p>We share data only with the providers we need to run the service, under contracts that limit their use of it:</p>
          <ul>
            <li>Messaging carriers and our SMS provider, to deliver texts and receive replies.</li>
            <li>Cloud hosting and storage, where the application and its database run.</li>
            <li>Email delivery, for sign-in codes, invitations and support replies.</li>
            <li>Machine translation, when an organization turns on multilingual sending; only the message text is sent for translation.</li>
          </ul>
          <p>We also disclose information when the law requires it, to protect the rights and safety of people using the service, or as part of a business transfer, with notice where required.</p>
          <p>Mobile numbers, text messaging opt-in data and consent information are excluded from all of the above: they are not sold, rented or shared with third parties or affiliates for marketing or promotional purposes, and are used only to deliver the messages you agreed to receive and to honor your opt-out.</p>
        </>
      ),
    },
    {
      id: 'retention',
      title: 'How long we keep it',
      body: (
        <p>Account data is kept while the account is active and for a reasonable period afterwards for audit purposes. Message history, delivery records and consent and opt-out records are kept for the life of the organization&rsquo;s account so that compliance can be demonstrated; opt-outs are retained after a contact is deleted so the number stays suppressed. Organizations can delete contacts and archive jobs at any time.</p>
      ),
    },
    {
      id: 'security',
      title: 'Security',
      body: <p>Data is encrypted in transit and at rest. Access is limited by role and job, sign-in supports one-time codes and two-step verification, sessions are short-lived and rotated, and administrative actions are logged. No system is perfectly secure; if we learn of a breach affecting your data we will notify the organization and, where required, you.</p>,
    },
    {
      id: 'rights',
      title: 'Your choices and rights',
      body: (
        <>
          <p>You can review and update your account details on the Account page, change how you sign in, and opt out of texts from any job by replying STOP. Depending on where you live you may also have rights to access, correct, delete or export your data, or to object to certain processing. Because the organization running your job controls its contact lists, we will route requests about them to that organization and help it respond.</p>
          <p>To exercise a right or ask a question, use the Help page or contact the organization&rsquo;s support address shown at the bottom of the sign-in page.</p>
        </>
      ),
    },
    {
      id: 'cookies',
      title: 'Cookies and device storage',
      body: (
        <>
          <p>{app} sets one strictly necessary cookie to keep you signed in; it is not used for advertising or cross-site tracking. Your theme, sidebar layout, remembered email and similar preferences are stored on your device only. No analytics or advertising cookies are set. You can review this at any time from the Cookies link in the footer.</p>
        </>
      ),
    },
    {
      id: 'children',
      title: 'Children',
      body: <p>The service is for people working on job sites and is not directed at children under 16. We do not knowingly collect information from them.</p>,
    },
    {
      id: 'changes',
      title: 'Changes to this policy',
      body: <p>When we change this policy we will update the effective date above and, for material changes, tell signed-in users in the app. Continued use after a change means you accept the updated policy.</p>,
    },
    {
      id: 'contact',
      title: 'Contact',
      body: (
        <p>
          Privacy questions and requests can be sent through the{' '}
          <Link to="/help" className="underline underline-offset-4">
            Help page
          </Link>
          , where your organization&apos;s administrators pick them up.
        </p>
      ),
    },
  ];
  const legal = useTenantLegal();
  // A static copy set in the console is the one canonical document: land there instead of rendering a second one here.
  useEffect(() => {
    const target = branding.legal.privacyUrl;
    if (target && target !== '/legal/privacy') window.location.replace(target);
  }, [branding.legal.privacyUrl]);
  const customBody = legal.data?.privacy.custom ? legal.data.privacy.body : null;
  const parsed = useMemo(() => (customBody ? parseLegalText(customBody) : null), [customBody]);
  return <Doc effective={legal.data?.effectiveDate ?? EFFECTIVE} eyebrow={`Legal · Effective ${legal.data?.effectiveDate ?? EFFECTIVE}`} title="Privacy policy" description={parsed?.intro ?? <>How {app} handles information about the people who use it and the people it texts.</>} sections={parsed ? parsed.sections : sections} other={{ href: branding.legal.termsUrl, fallback: '/legal/terms', label: 'Terms of service' }} />;
}

export function TermsPage() {
  const { branding } = useBranding();
  const app = branding.appName;
  const sections: Section[] = [
    {
      id: 'agreement',
      title: 'The agreement',
      body: (
        <p>These terms govern use of {app}, a job-site messaging service provided to organizations (&ldquo;customers&rdquo;) and used by the people they invite (&ldquo;users&rdquo;). By signing in or using the service you agree to them on your own behalf and, where you act for an organization, on its behalf. If your organization has a separate written agreement with us, that agreement controls where the two conflict.</p>
      ),
    },
    {
      id: 'service',
      title: 'The service',
      body: (
        <>
          <p>Each job gets a dedicated phone number, a contact list, a message history and a team. Job admins compose and send texts; every message carries the sender label and the opt-out instruction required by carriers. Replies such as STOP and START are processed automatically. Optional features include multilingual sending, information keywords, self-service sign-up at the gate, weather and schedule automation, and delivery reporting.</p>
          <p>We may improve or change features over time. If a change materially reduces the service, we will give customers reasonable notice.</p>
        </>
      ),
    },
    {
      id: 'accounts',
      title: 'Accounts and access levels',
      body: (
        <>
          <p>Accounts are created by invitation. Keep your credentials confidential, turn on two-step verification when your organization asks for it, and tell your administrator promptly if you suspect misuse. You are responsible for activity under your account.</p>
          <p>Organizations assign access levels: Super Users manage the organization, PM Admins run their jobs including sending, and PM Users work on jobs without sending. Only people with sending rights may send texts.</p>
        </>
      ),
    },
    {
      id: 'customer',
      title: 'Customer responsibilities',
      body: (
        <>
          <p>Texting people requires their consent. As a customer you are responsible for:</p>
          <ul>
            <li>having appropriate consent, under the TCPA, CTIA guidelines and any other applicable law, for every number you add to a job, and keeping records of it (the service stores the consent records you provide);</li>
            <li>sending only operational, job-related messages that match what recipients agreed to, and never marketing or promotional content unless you hold separate marketing consent;</li>
            <li>keeping contact data accurate and removing people who leave the job;</li>
            <li>honoring opt-outs; the service suppresses numbers that reply STOP and you must not add them back without a new request from the person; and</li>
            <li>the content your users send, and the people you give access to.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'acceptable',
      title: 'Acceptable use',
      body: (
        <>
          <p>Do not use the service to send unlawful, threatening, harassing, deceptive or discriminatory content; to send messages unrelated to the job the recipient joined; to circumvent opt-outs, carrier rules or rate limits; to probe or disrupt the service; or to upload malicious files. We may pause sending, remove content or suspend accounts to protect recipients, carriers and other customers.</p>
        </>
      ),
    },
    {
      id: 'fees',
      title: 'Fees and pilots',
      body: <p>Fees, pilot periods and message allowances are set out in the customer&rsquo;s order or pilot agreement. Carrier and registration costs may be passed through where the order says so. Pilot accounts may be limited in duration and volume and may be closed at the end of the pilot unless converted.</p>,
    },
    {
      id: 'data',
      title: 'Your data',
      body: (
        <>
          <p>Customers own their contact lists, message content and job information. We process that data only to provide, secure and support the service, as described in the{' '}
            <Link to="/legal/privacy" className="underline underline-offset-4">
              privacy policy
            </Link>
            . Customers can export their data during the term and for thirty days after termination, after which we may delete it, except for records we must keep by law or for opt-out suppression.</p>
        </>
      ),
    },
    {
      id: 'availability',
      title: 'Availability and support',
      body: (
        <p>
          We aim to keep the service available around the clock and publish its condition on the{' '}
          <Link to="/status" className="underline underline-offset-4">
            status page
          </Link>
          . Delivery of individual texts depends on carriers and the recipient&rsquo;s device, which we do not control. Support is available through the Help page.
        </p>
      ),
    },
    {
      id: 'suspension',
      title: 'Suspension and termination',
      body: <p>Either party may end the agreement as the order allows. We may suspend or terminate access immediately for a serious breach of these terms, for non-payment, or where required by a carrier or the law. On termination, sending stops and the data provisions above apply.</p>,
    },
    {
      id: 'ip',
      title: 'Our intellectual property',
      body: <p>The service, its software, design and documentation belong to us and our licensors. You receive a limited right to use them during the term. You may not copy, modify, reverse engineer or resell the service, or remove any notices.</p>,
    },
    {
      id: 'disclaimer',
      title: 'Disclaimers and limitation of liability',
      body: (
        <>
          <p>The service is provided &ldquo;as is&rdquo; to the extent the law allows. We do not warrant that messages will be delivered at any particular time, that weather or other automated notices will be complete or accurate, or that the service will be uninterrupted. Safety-critical decisions must not rely solely on the service.</p>
          <p>To the extent permitted by law, neither party is liable for indirect, incidental, special or consequential damages, and our total liability arising out of the service in any twelve-month period is limited to the fees paid for the service in that period. These limits do not apply to a party&rsquo;s indemnity obligations, breach of confidentiality, or liability that cannot be limited by law.</p>
        </>
      ),
    },
    {
      id: 'law',
      title: 'Governing law and changes',
      body: (
        <>
          <p>These terms are governed by the laws of the State of Florida, without regard to conflict-of-law rules, and disputes will be brought in the state or federal courts located there unless the customer&rsquo;s written agreement says otherwise.</p>
          <p>We may update these terms; the effective date above will change and material updates will be announced in the app. Continued use after the effective date means you accept the updated terms.</p>
        </>
      ),
    },
  ];
  const legal = useTenantLegal();
  useEffect(() => {
    const target = branding.legal.termsUrl;
    if (target && target !== '/legal/terms') window.location.replace(target);
  }, [branding.legal.termsUrl]);
  const customBody = legal.data?.terms.custom ? legal.data.terms.body : null;
  const parsed = useMemo(() => (customBody ? parseLegalText(customBody) : null), [customBody]);
  return <Doc effective={legal.data?.effectiveDate ?? EFFECTIVE} eyebrow={`Legal · Effective ${legal.data?.effectiveDate ?? EFFECTIVE}`} title="Terms of service" description={parsed?.intro ?? <>The rules for using {app}, for organizations and for the people they invite.</>} sections={parsed ? parsed.sections : sections} other={{ href: branding.legal.privacyUrl, fallback: '/legal/privacy', label: 'Privacy policy' }} />;
}
